import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Col, Empty, Row, Statistic, Tabs } from 'antd'
import { Doughnut } from 'react-chartjs-2'
import { colorAt, doughnutData, doughnutOptions, formatMoney } from './utils'
import { conversationPath } from '../DutyAssistant/statusColors'

// What each service calls an order, so "Orders" reads right on every tab.
const ORDER_NOUN = {
  duty: 'Cars checked',
  vehicle_history: 'Report orders',
  tracking: 'Shipments'
}

const STAGE_TITLE = {
  duty: 'Conversations by stage',
  vehicle_history: 'Orders by status',
  tracking: 'Shipments by status'
}

/**
 * Every service the assistant sells, one tab each (owner, 2026-10-29): the same five numbers for
 * every module — conversations, orders, paid, conversion and revenue — and that module's own
 * stage breakdown. One card rather than a section per module, so the overview does not grow by a
 * screen every time a service is added.
 */
const ServiceBreakdown = ({ services, onOpenConversations }) => {
  const list = services || []
  const [active, setActive] = useState(undefined)
  if (!list.length) return null
  const current = list.find(s => s.key === active) || list[0]

  const stages = current.stages || { total: 0, segments: [] }
  const stat = (title, value, extra) => (
    <Col xs={12} md={8} xl={4} key={title}>
      <Statistic title={title} value={value} />
      {extra ? <div style={{ color: '#888', fontSize: 12 }}>{extra}</div> : null}
    </Col>
  )

  return (
    <Card
      title="Services"
      bodyStyle={{ paddingTop: 0 }}
      extra={
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => onOpenConversations(conversationPath(current.key))}
        >
          Open conversations
        </Button>
      }
    >
      <Tabs
        activeKey={current.key}
        onChange={setActive}
      >
        {list.map(s => (
          <Tabs.TabPane tab={s.label} key={s.key} />
        ))}
      </Tabs>

      <Row gutter={[16, 16]}>
        {stat('Conversations', current.conversations, 'With the assistant')}
        {stat(
          ORDER_NOUN[current.key] || 'Orders',
          current.orders,
          `${current.whatsappOrders} from WhatsApp`
        )}
        {stat('Paid', current.paid)}
        {stat('Conversion', `${current.conversion}%`, 'Paid of orders')}
        <Col xs={24} md={16} xl={8}>
          <Statistic
            title="Revenue (paid)"
            value={formatMoney(current.revenue, current.currency)}
            valueStyle={{ color: '#3f8600' }}
          />
        </Col>
      </Row>

      <div style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {STAGE_TITLE[current.key] || 'By status'}
        </div>
        {stages.total > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 140, height: 140, flexShrink: 0 }}>
              <Doughnut data={doughnutData(stages.segments)} options={doughnutOptions} />
            </div>
            <div style={{ flex: 1, minWidth: 200, maxWidth: 420 }}>
              {stages.segments
                .filter(seg => seg.count > 0)
                .map(seg => (
                  <div
                    key={seg.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: colorAt(stages.segments.indexOf(seg)),
                        flexShrink: 0
                      }}
                    />
                    <span style={{ flex: 1 }}>{seg.label}</span>
                    <span style={{ fontWeight: 600 }}>{seg.count}</span>
                    <span style={{ width: 56, textAlign: 'right', color: '#888' }}>
                      {seg.percentage}%
                    </span>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <Empty description="No data for this period" />
        )}
      </div>
    </Card>
  )
}

ServiceBreakdown.propTypes = {
  services: PropTypes.array,
  onOpenConversations: PropTypes.func.isRequired
}

export default ServiceBreakdown
